import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Container,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Checkbox,
  Divider,
  Card,
  CardContent,
  Grid,
  Link,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import QrCodeIcon from "@mui/icons-material/QrCode";
import useAxiosInstance from "../../hooks/useAxiosInstance";
import { API_RESULT, API_VERSION } from "../../enums/common.enum";
import { useLocation } from "react-router-dom";
import { errorToast, handleError, successToast } from "../../utils/common";
import { addDays, format } from "date-fns";
import { Backdrop, CircularProgress } from "@mui/material";
import PaymentProcessingModal from "./PaymentProcessingModal";

// interface Purchase {
//   id: number;
//   distributor: string;
//   invoiceNumber: string;
//   dueDate: string;
//   amount: number;
//   originalAmount: number;
//   selected: boolean;
// }

const MakePayment = () => {
  // Sample data for pending purchases
  const [purchases, setPurchases] = useState<any[]>([]);
  const [filterBy, setFilterBy] = useState("all");
  const [paymentMethod, setPaymentMethod] = useState("upi");
  const [open, setOpen] = useState(false);

  const simulateStatusCheck = async (activeInvId: String) => {
    try {
      const result = await getSinglePurchaseData(activeInvId);
      return result;
    } catch (error) {
      return { status: "failed", message: "Payment could not be completed. Please try again or contact MedPay for support." };
    }
  };

  const getSinglePurchaseData = async (activeInvId: String) => {
    setLoading(true);
    try {
      const response = await privateInstance.get(
        `${API_VERSION.V3}/suppliers/purchases?purchase_id=${activeInvId}`
      );

      if (response?.data?.status === API_RESULT.SUCCESS) {
        if (response?.data?.response?.payment_status) {
          if (response?.data?.response?.payment_status === API_RESULT.SUCCESS || response?.data?.response?.payment_status === "processing") {
            return { status: "success" };
          } else if (response?.data?.response?.payment_status === "pending") {
            return { status: "pending" };
          } else {
            return { status: "failed" };
          }
        } else {
          return { status: "failed" };
        }
      } else {
        return { status: "failed" };
      }
    } catch (err) {
      handleError(err);
      return { status: "failed" };
    }
  };

  const handleTogglePurchase = (id: number) => {
    setPurchases(
      purchases.map((purchase) =>
        purchase.purchase_id === id
          ? { ...purchase, selected: !purchase.selected }
          : purchase
      )
    );
  };

  const handlePaymentMethodChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPaymentMethod(event.target.value);
  };

  // Calculate total amount to pay
  const totalAmount = purchases
    .filter((purchase) => purchase.selected)
    .reduce((sum, purchase) => sum + Number(purchase.payable_amount), 0);

  // Calculate number of purchases selected
  const selectedCount = purchases.filter(
    (purchase) => purchase.selected
  ).length;

  const [loading, setLoading] = useState(false);
  const privateInstance = useAxiosInstance();
  const location = useLocation();
  const params: any = new URLSearchParams(location.search);
  const locationId = params.get("locationId");

  const getPurchaseData = async () => {
      setLoading(true);
      try {
        const result = await privateInstance.get(
          `${API_VERSION.V3}/suppliers/purchases?location_id=${locationId}`
        );
        if (result?.data?.status === API_RESULT.SUCCESS) {
           const enrichedPurchases = result.data.response.map((purchase: any) => ({
              ...purchase,
              selected: false,
            }));
            setPurchases(enrichedPurchases);
        } else {
          errorToast(result.data.message);
        }
      } catch (err) {
        handleError(err);
      } finally {
        setLoading(false);
      }
    };

  useEffect(() => {
    getPurchaseData();

    //set filter from param
    if (params.get("type")) {
      setFilterBy(params.get("type"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtredPurchases = purchases.filter((x) =>
    filterBy === "all"
      ? x.payment_status === "pending"
      : filterBy === "today"
      ? x.due_date === format(new Date(), "dd-MM-yyyy")
      : filterBy === "tomorrow"
      ? x.due_date === format(addDays(new Date(), 1), "dd-MM-yyyy")
      : x
  );

  const handlePaymentClick = async () => {
    const selectedPurchaseIds = purchases
      .filter((purchase) => purchase.selected)
      .map((purchase) => purchase.purchase_id);

    let payload = {
      type: "gen_txn",
      location_id: locationId,
      purchases: selectedPurchaseIds,
    };
    setLoading(true);
    try {
      const result = await privateInstance.post(
        `${API_VERSION.V3}/suppliers/purchases`,
        payload
      );
      if (result?.data?.status === API_RESULT.SUCCESS) {
        if (result.data?.response) {
          setOpen(true);
          window.location.href = `upi://pay?pa=MEDWAY0390@icici&pn=MedPay&am=${totalAmount}&cu=INR&tr=${result.data.response}&mc=5912`;
        }
      } else {
        errorToast(result.data.message);
      }
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h5" fontWeight="bold" sx={{ mb: 1 }}>
                Pending Purchases
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                Select purchases you want to pay
              </Typography>
            </Box>

            <FormControl size="small">
              <InputLabel id="filter-by-label">Filter By</InputLabel>
              <Select
                labelId="filter-by-label"
                name="filter-by"
                id="filter-by"
                label="Filter By"
                value={filterBy}
                onChange={(e) => {
                  setFilterBy(e.target.value);
                  //reset  selected
                  setPurchases(
                    purchases.map((purchase) => ({
                      ...purchase,
                      selected: false,
                    }))
                  );
                }}
                sx={{ textTransform: "capitalize" }}
              >
                {["all", "today", "tomorrow"].map((x: string) => {
                  return (
                    <MenuItem key={x} value={x}>
                      {x}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Box>

          {filtredPurchases?.length ? (
            <>
              {filtredPurchases.map((purchase) => (
                <Card
                  key={`${purchase.purchase_id}-${purchase.supplier_name}`}
                  variant="outlined"
                  sx={{
                    mb: 2,
                    borderRadius: 2,
                    cursor: "pointer",
                    border: "1px solid #e0e0e0",
                  }}
                  onClick={() => handleTogglePurchase(purchase.purchase_id)}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Grid
                      container
                      justifyContent="space-between"
                      alignItems="flex-start"
                    >
                      <Grid item xs>
                        <Typography
                          variant="h6"
                          fontWeight="bold"
                          sx={{ mb: 0.5 }}
                        >
                          {purchase.supplier_name}
                        </Typography>
                        <Typography color="text.secondary">
                          Invoice #{purchase.purchase_id}
                        </Typography>
                        <Box
                          sx={{ display: "flex", alignItems: "center", mt: 1 }}
                        >
                          <CalendarTodayIcon
                            fontSize="small"
                            sx={{
                              mr: 0.5,
                              color: "text.secondary",
                              fontSize: 16,
                            }}
                          />
                          <Typography variant="body2" color="text.secondary">
                            Due: {purchase.due_date}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item>
                        <Box sx={{ textAlign: "right" }}>
                          <Typography variant="h6" fontWeight="bold">
                            ₹{purchase.payable_amount?.toLocaleString()}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Invoice: ₹{purchase.payable_amount?.toLocaleString()}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            mt: 1,
                            display: "flex",
                            justifyContent: "flex-end",
                          }}
                        >
                          <Checkbox
                            checked={purchase.selected}
                            onChange={() =>
                              handleTogglePurchase(purchase.purchase_id)
                            }
                            sx={{ p: 0 }}
                          />
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              ))}
            </>
          ) : (
            <Typography variant="caption">No Data Found</Typography>
          )}
        </Paper>

        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="h5" fontWeight="bold" sx={{ mb: 1 }}>
            Payment Details
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Select payment method
          </Typography>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6">Amount to Pay</Typography>
            <Typography variant="h6" fontWeight="bold">
              ₹{totalAmount.toLocaleString()}
            </Typography>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {selectedCount} purchase(s) selected
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" sx={{ mb: 2 }}>
            Payment Method
          </Typography>

          <FormControl component="fieldset" fullWidth>
            <RadioGroup
              aria-label="payment-method"
              name="payment-method"
              value={paymentMethod}
              onChange={handlePaymentMethodChange}
            >
              <Paper
                variant="outlined"
                sx={{
                  mb: 2,
                  borderRadius: 2,
                  border:
                    paymentMethod === "upi"
                      ? "1px solid #0058ff"
                      : "1px solid #e0e0e0",
                  bgcolor:
                    paymentMethod === "upi"
                      ? "rgba(0, 88, 255, 0.04)"
                      : "transparent",
                }}
              >
                <FormControlLabel
                  value="upi"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <QrCodeIcon sx={{ mr: 1, color: "#f6a609" }} />
                      <Typography>UPI Payment</Typography>
                    </Box>
                  }
                  sx={{ p: 2, width: "100%", m: 0 }}
                />
              </Paper>

              {/*<Paper
                variant="outlined"
                sx={{
                  mb: 2,
                  borderRadius: 2,
                  border:
                    paymentMethod === "credit"
                      ? "1px solid #0058ff"
                      : "1px solid #e0e0e0",
                  bgcolor:
                    paymentMethod === "credit"
                      ? "rgba(0, 88, 255, 0.04)"
                      : "transparent",
                }}
              >
                <FormControlLabel
                  disabled
                  value="credit"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <CreditCardIcon sx={{ mr: 1, color: "#1976d2" }} />
                      <Typography>Credit Card</Typography>
                    </Box>
                  }
                  sx={{ p: 2, width: "100%", m: 0 }}
                />
              </Paper>*/}

              <Paper
                variant="outlined"
                sx={{
                  mb: 2,
                  borderRadius: 2,
                  border:
                    paymentMethod === "debit"
                      ? "1px solid #0058ff"
                      : "1px solid #e0e0e0",
                  bgcolor:
                    paymentMethod === "debit"
                      ? "rgba(0, 88, 255, 0.04)"
                      : "transparent",
                }}
              >
                <FormControlLabel
                  disabled
                  value="debit"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <AccountBalanceWalletIcon
                        sx={{ mr: 1, color: "#4caf50" }}
                      />
                      <Typography>Debit Card</Typography>
                    </Box>
                  }
                  sx={{ p: 2, width: "100%", m: 0 }}
                />
              </Paper>

              {/* <Paper
                variant="outlined"
                sx={{
                  mb: 2,
                  borderRadius: 2,
                  border:
                    paymentMethod === "netbanking"
                      ? "1px solid #0058ff"
                      : "1px solid #e0e0e0",
                  bgcolor:
                    paymentMethod === "netbanking"
                      ? "rgba(0, 88, 255, 0.04)"
                      : "transparent",
                }}
              >
                <FormControlLabel
                  value="netbanking"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <AccountBalanceIcon sx={{ mr: 1, color: "#9c27b0" }} />
                      <Typography>Net Banking</Typography>
                    </Box>
                  }
                  sx={{ p: 2, width: "100%", m: 0 }}
                />
              </Paper> */}
            </RadioGroup>
          </FormControl>

          {/* <Box display="flex" gap={1.5}>
            <Link href="upi://pay?pa=epos17150b02h.cfqr@axisbank&amp;am=2&amp;cu=INR&amp;tn=4322323423">
              Pay Now{" "}
            </Link>
            <Link href="phonepe://pay?pa=epos17150b02h.cfqr@axisbank&amp;am=2&amp;cu=INR&amp;tn=4322323423">
              Phonepe{" "}
            </Link>
            <Link href="paytmmp://pay?pa=epos17150b02h.cfqr@axisbank&amp;am=2&amp;cu=INR&amp;tn=4322323423">
              paytmmp{" "}
            </Link>
            <Link href="tez://upi/pay?pa=epos17150b02h.cfqr@axisbank&amp;am=2&amp;cu=INR&amp;tn=4322323423">
              Gpay{" "}
            </Link>
          </Box> */}

          {totalAmount === 0 ? (
            <Button
              fullWidth
              variant="contained"
              disabled
              sx={{
                mt: 2,
              }}
            >
              Pay ₹{totalAmount.toLocaleString()}
            </Button>
          ) : (
            <Link
              onClick={handlePaymentClick}
              sx={{
                display: "block",
                width: "100%",
                mt: 2,
                py: 1.5,
                color: "#fff",
                textAlign: "center",
                bgcolor: "#0058ff",
                "&:hover": {
                  bgcolor: "#0046cc",
                },
                borderRadius: 1,
              }}
            >
              Pay ₹{totalAmount.toLocaleString()}
            </Link>
          )}
        </Paper>

        <Backdrop
          open={loading}
          sx={{
            color: "#fff",
            zIndex: (theme) => theme.zIndex.drawer + 1,
            backdropFilter: "blur(2px)",
          }}
        >
          <CircularProgress sx={{ color: "#0058ff" }} />
        </Backdrop>
      </Container>

      {open ? (
        <PaymentProcessingModal
          open={open}
          onClose={() => {
            setOpen(false);
            getPurchaseData(); //to update list
          }}
          checkPaymentStatus={() => {
            const selected = purchases.find((p) => p.selected);
            if (!selected) {
              return Promise.resolve({
                status: "failed",
                message: "No selected purchase to check.",
              });
            }
            return simulateStatusCheck(selected.purchase_id);
          }}
        />
      ) : null}
    </>
    
  );
};

export default MakePayment;
